/**
 * Adresses e-mail : une seule règle de saisie pour l'API et l'interface.
 *
 * Une adresse copiée depuis un message ou dictée sur un téléphone arrive souvent
 * avec une majuscule, une espace ou un caractère invisible en bordure. On retire
 * ces bords et on passe en minuscules — rien d'autre : une espace AU MILIEU
 * n'est jamais supprimée, l'adresse est refusée pour que la personne la corrige
 * plutôt que d'écrire à quelqu'un d'autre.
 *
 * Les colonnes concernées sont en `citext` : l'unicité y est déjà insensible à
 * la casse. La normalisation évite seulement d'enregistrer des variantes.
 */
import { z } from 'zod';

export const EMAIL_MAX_LENGTH = 254;
export const EMAIL_INVALID_MESSAGE =
  'Adresse e-mail invalide. Vérifiez qu’elle ressemble à nom@exemple.fr, sans espace.';

/** Espaces, tabulations, retours à la ligne et caractères invisibles de bordure. */
const EDGES = /^[\s\u200B-\u200D\u2060\uFEFF]+|[\s\u200B-\u200D\u2060\uFEFF]+$/g;
/** Volontairement simple : une partie locale, un domaine avec au moins un point. */
const SHAPE = /^[a-z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-z0-9](?:[a-z0-9-]*[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]*[a-z0-9])?)+$/;

export function normalizeEmail(value: string): string {
  return value.replace(EDGES, '').toLowerCase();
}

export function isValidEmail(value: string): boolean {
  const email = normalizeEmail(value);
  if (email.length === 0 || email.length > EMAIL_MAX_LENGTH) return false;
  if (!SHAPE.test(email)) return false;
  const local = email.slice(0, email.indexOf('@'));
  return local.length <= 64 && !local.startsWith('.') && !local.endsWith('.') && !local.includes('..');
}

/** Schéma serveur : valide puis rend l'adresse normalisée. */
export const emailSchema = z
  .string({ error: EMAIL_INVALID_MESSAGE })
  .max(1024, EMAIL_INVALID_MESSAGE)
  .transform(normalizeEmail)
  .refine(isValidEmail, EMAIL_INVALID_MESSAGE);

/**
 * Champ de formulaire : `''` si le champ est vide (adresse facultative),
 * l'adresse normalisée si elle est valide, `null` si elle est à corriger.
 */
export function parseEmailInput(value: string): string | null {
  const email = normalizeEmail(value);
  if (email === '') return '';
  return isValidEmail(email) ? email : null;
}
